import { Grid, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCurrentCompanyDetails } from '@/services/paymentsQuery';
import { useUser } from '@/contexts/user/UserContext';
import { hasSettingsAccess } from '@/utils/permissions';
import {
    DATA_LON,
    DATEV_LODAS_FILE,
    LESSOR,
    supportsTaxCardUpload
} from '@/utils/constant';
import PaymentsAPIServiceV2 from '@/services/PaymentsServiceV2';
import {
    ImportSummary,
    SalaryImportCard
} from '@/pages/company/salary/SalaryImportCard';
import {
    DataLonImportResponse,
    DataLonImportResultDialog
} from '@/pages/company/salary/DataLonImportResultDialog';
import {
    TaxCardUploadResponse,
    TaxCardUploadResultDialog
} from '@/pages/company/salary/TaxCardUploadResultDialog';

// Client-side cap matching the bank-details importers' backend limit. Exports
// for a single company are small in practice; this catches an accidental
// wrong-file pick before bytes go out.
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

const summarizeDataLon = (response: DataLonImportResponse): ImportSummary => ({
    total: response.totalRecords,
    updated: response.processed,
    failed: response.failed,
    errors: response.errors
});

// Employees are identified by their payroll number only — never by name or IBAN.
const toErrorLines = (response: TaxCardUploadResponse) =>
    response.errors.map((error) =>
        `${error.employeeNumber ?? ''} ${error.errorMessage}`.trim()
    );

const summarizeDatevLodas = (
    response: TaxCardUploadResponse
): ImportSummary => ({
    total: response.totalRows,
    updated: response.successfulImports,
    failed: response.failedImports,
    errors: toErrorLines(response)
});

/**
 * Only the Lessor branch of the tax-card endpoint returns a body; the SKAT
 * branch acknowledges with a bodiless 200. Reading that as zero rows would
 * report every successful SKAT import as having changed nothing, so the absent
 * body maps to `null` — accepted, with no per-row detail to show.
 */
const summarizeTaxCard = (
    response: TaxCardUploadResponse
): ImportSummary | null =>
    typeof response?.totalRows !== 'number'
        ? null
        : {
              total: response.totalRows,
              updated: response.successfulImports,
              failed: response.failedImports,
              errors: toErrorLines(response)
          };

/**
 * Dashboard entry point for the manual salary-file uploads.
 *
 * Every company gets exactly one card: the two file-based payroll systems get
 * their own bank-details import, everyone else gets the tax-card upload. The
 * cards share a generic "File upload" heading and name the expected file in the
 * supporting copy, so the block reads the same whichever system a company is on.
 *
 * The tax-card card narrows its accepted extension by provider. Both parsers sit
 * behind one endpoint, and the Lessor branch silently skips anything that is not
 * a .csv — offering the wrong extension buys a 200 with no body and no import.
 *
 * Renders nothing until the salary provider is known, so the card cannot appear
 * and then swap for a company that turns out to be on DataLon or DATEV LODAS.
 */
export const SalaryFileUpload = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { user } = useUser();
    const { data: companyDetails } = useCurrentCompanyDetails();

    const salaryProvider = companyDetails?.data?.salaryProvider;

    // Importing bank details is company-wide configuration, so it is gated to
    // the same roles that can open Settings. Managers reach the Dashboard but
    // are scoped to their own departments and must not see this.
    if (!hasSettingsAccess(user?.role) || !salaryProvider) {
        return null;
    }

    const isLessor = salaryProvider === LESSOR;

    return (
        <Grid
            container
            size={12}
            mt={4}
            gap={2}
            sx={{
                // The stat row above is three `lg: 3.8` cards in a gap-spaced
                // flex container, so it stops short of full width: 3 × 3.8/12
                // (95%) plus the two 16px gaps between them. Matching that keeps
                // this card's right edge flush with the cards above. Below `lg`
                // those cards stack full-width, so this one does too.
                width: { xs: '100%', lg: `calc(95% + ${theme.spacing(4)})` }
            }}
        >
            {supportsTaxCardUpload(salaryProvider) && (
                <SalaryImportCard<TaxCardUploadResponse>
                    title={t('salary_file_upload_title')}
                    info={t('upload_tax_card_info')}
                    confirmationNote={t('tax_card_upload_confirmation_note')}
                    accept={
                        isLessor
                            ? { 'text/csv': ['.csv'] }
                            : { 'text/plain': ['.txt'] }
                    }
                    constraints={t(
                        isLessor
                            ? 'tax_card_upload_constraints_lessor'
                            : 'tax_card_upload_constraints_skat'
                    )}
                    maxFileSizeBytes={MAX_FILE_SIZE_BYTES}
                    upload={(file, onProgress) =>
                        PaymentsAPIServiceV2.getInstance().uploadTaxCard(
                            file,
                            onProgress
                        )
                    }
                    summarize={summarizeTaxCard}
                    renderResultDialog={({ open, result, onClose }) => (
                        <TaxCardUploadResultDialog
                            open={open}
                            uploadResult={result}
                            onClose={onClose}
                        />
                    )}
                    alerts={{
                        success: t('tax_card_upload_success'),
                        partial: t('tax_card_upload_partial_success'),
                        failed: t('tax_card_upload_failed'),
                        accepted: t('tax_card_upload_accepted')
                    }}
                />
            )}

            {salaryProvider === DATA_LON && (
                <SalaryImportCard<DataLonImportResponse>
                    title={t('salary_file_upload_title')}
                    info={t('data_lon_upload_info')}
                    confirmationNote={t('data_lon_upload_confirmation_note')}
                    accept={{ 'text/csv': ['.csv'] }}
                    constraints={t('data_lon_upload_constraints')}
                    maxFileSizeBytes={MAX_FILE_SIZE_BYTES}
                    upload={(file, onProgress) =>
                        PaymentsAPIServiceV2.getInstance().uploadDataLonEmployeesFile(
                            file,
                            onProgress
                        )
                    }
                    summarize={summarizeDataLon}
                    renderResultDialog={({ open, result, onClose }) => (
                        <DataLonImportResultDialog
                            open={open}
                            uploadResult={result}
                            onClose={onClose}
                        />
                    )}
                    alerts={{
                        success: t('data_lon_upload_success'),
                        partial: t('data_lon_upload_partial_success'),
                        failed: t('data_lon_upload_failed')
                    }}
                />
            )}

            {salaryProvider === DATEV_LODAS_FILE && (
                <SalaryImportCard<TaxCardUploadResponse>
                    title={t('salary_file_upload_title')}
                    info={t('datev_lodas_upload_info')}
                    confirmationNote={t('datev_lodas_upload_confirmation_note')}
                    accept={{
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                            ['.xlsx']
                    }}
                    constraints={t('datev_lodas_upload_constraints')}
                    maxFileSizeBytes={MAX_FILE_SIZE_BYTES}
                    upload={(file, onProgress) =>
                        PaymentsAPIServiceV2.getInstance().uploadDatevLodasFile(
                            file,
                            onProgress
                        )
                    }
                    summarize={summarizeDatevLodas}
                    renderResultDialog={({ open, result, onClose }) => (
                        <TaxCardUploadResultDialog
                            open={open}
                            uploadResult={result}
                            onClose={onClose}
                        />
                    )}
                    alerts={{
                        success: t('datev_lodas_upload_success'),
                        partial: t('datev_lodas_upload_partial_success'),
                        failed: t('datev_lodas_upload_failed')
                    }}
                />
            )}
        </Grid>
    );
};
